// @ts-strict-ignore
import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Banner,
  Box,
  Icon,
  IconName,
  Spinner,
  Table,
  Text,
  makeInitialTableState,
  useTableLocalStorage,
} from '@usealloy/component-library';

import { capitalize } from '~/analytics/utils/helpers';
import type { Dimension } from '~/application-review/redux/types';
import { formatCurrencyIntl } from '~/helpers/i18n';

import { useActionEvidences, useThresholdEvidenceExportCsv } from './hooks';
import { StyledEvidencesSection } from './styles';
import { UserContext } from '~/dashboard/context';

export type EvidencesTableProps = {
  thresholdId: number;
  actionId: number;
  entityToken: string;
  evaluationToken: string;
  isPortfolioEvaluation: boolean;
  dimensions: Dimension[];
};

const EvidencesTable = ({
  thresholdId,
  actionId,
  entityToken,
  evaluationToken,
  isPortfolioEvaluation,
  dimensions,
}: EvidencesTableProps) => {
  const {
    data,
    isSuccess,
    isLoading,
    isError,
  } = useActionEvidences({
    entityToken,
    evaluationToken,
    actionId,
    isPortfolioEvaluation,
    dimensions,
  });
  const { featureFlags } = useContext(UserContext);

  const targetThreshold = useMemo(() => {
    if (!thresholdId || !isSuccess) {
      return null;
    }

    // eslint-disable-next-line no-underscore-dangle
    const allThresholds = data?._embedded?.thresholds || [];

    const threshold = allThresholds.find(t => (
      t.action_threshold_id === thresholdId
    ));

    return threshold || null;
  }, [data, isSuccess, thresholdId]);

  const evidence = useMemo(() => ({
    data: targetThreshold?.evidence?.data || [],
    displayName: targetThreshold?.attribute_display_name || '',
  }), [targetThreshold]);

  const {
    mutate: exportCSV,
    isLoading: isExportingCSV,
  } = useThresholdEvidenceExportCsv({
    entityToken,
    evaluationToken,
    actionId,
    thresholdId,
    displayName: evidence.displayName,
    isPortfolioEvaluation,
    dimensions,
  });

  /**
   * Evidences columns are pulled from the `MLA_TO_COLUMN_DEF_MAP` in Typhon by
   * the MLA name, so we store them here in local storage by the same name so that
   * the evidence table looks the same for the same MLAs
   */
  const { getStateFromLocalStorage, saveStateToLocalStorage } = useTableLocalStorage(
    `rule-explainability-evidences-${targetThreshold?.attribute_internal_name}`,
  );

  const [tableState, setTableState] = useState({
    ...makeInitialTableState({
      pagination: { pageSize: 10, pageIndex: 0 },
      sorting: [{ id: 'created_at', desc: true }],
      columnVisibility: {
        is_sandbox: false,
        submitted_at: false,
      },
    }),
    ...getStateFromLocalStorage(),
  });

  const columnDefs = useMemo(() => {
    const columnDefsRaw = targetThreshold?.evidence?.column_definition || {};

    const columnDefsFormatted = (Object.entries(columnDefsRaw)).map(([key, value]) => ({
      id: key,
      header: value?.display_name ?? capitalize(key.replaceAll('_', ' ')),
      accessorKey: key,
      accessorFn: accessorValue => {
        if (!accessorValue[key]) {
          return '-';
        }

        switch (value?.value_type) {
          case 'timestamp':
            return new Date(accessorValue[key]).toLocaleDateString();
          case 'number':
            return new Intl.NumberFormat().format(accessorValue[key]);
          case 'currency':
            return formatCurrencyIntl(accessorValue[key], {
              alphabeticCurrencyCode: accessorValue?.currency_code || 'USD',
            }).formattedNumber;
          default:
            return accessorValue[key];
        }
      },
    }));

    return columnDefsFormatted || [];
  }, [targetThreshold]);

  /**
   * If `targetThreshold` is `null` or changes after the call to `useTableLocalStorage`
   * we'll need to update the `tableState` using the latest `getStateFromLocalStorage`
   */
  useEffect(() => {
    if (targetThreshold?.attribute_internal_name) {
      setTableState({
        ...tableState,
        ...getStateFromLocalStorage(),
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Do not include tableState in dep array as it will trigger an infinite update loop
    getStateFromLocalStorage,
    targetThreshold?.attribute_internal_name,
  ]);

  const tableSection = useMemo(() => {
    if (isLoading) {
      return (
        <Box>
          <Spinner />
        </Box>
      );
    }

    if (isError) {
      return (
        <Banner
          open
          outline
          borderColor="var(--cl-red-300)"
          variant="error"
        >
          There was an error fetching evidence for this threshold
        </Banner>
      );
    }

    return (
      <Table
        columns={columnDefs}
        data={evidence?.data.slice(
          (tableState.pagination.pageSize * tableState.pagination.pageIndex),
          (tableState.pagination.pageSize * (tableState.pagination.pageIndex + 1)),
        )}
        disablePageSizeSelection
        emptyProps={{
          text: 'No evidence found for this threshold',
        }}
        enableColumnHiding
        enableColumnResizing
        enablePagination
        enableSorting
        onTableStateChange={newState => {
          setTableState(newState);
          saveStateToLocalStorage(newState);
        }}
        state={tableState}
        recordCount={evidence?.data.length}
        toolbarTitle={(
          <>
            <div style={{ minHeight: '16px', minWidth: '16px' }}>
              <Icon icon={IconName.Receipt} size="small" />
            </div>
            <Text weight={700} style={{ textWrap: 'nowrap' }}>
              {evidence?.displayName ? `Evidences for ${evidence.displayName}` : 'Evidences'}
            </Text>
          </>
        )}
        toolbarActions={[{
          onClick: () => {
            if (!isExportingCSV) {
              exportCSV();
            }
          },
          name: 'Export CSV',
        }]}
      />
    );
  }, [
    evidence,
    exportCSV,
    columnDefs,
    isError,
    isExportingCSV,
    isLoading,
    saveStateToLocalStorage,
    tableState,
  ]);

  return (
    <StyledEvidencesSection
      direction="column"
      alignItems="flex-start"
      evaluationExplainability={featureFlags.ruleExplainabilityPhase >= 4}
    >
      {tableSection}
    </StyledEvidencesSection>
  );
};

export default EvidencesTable;
